import NewSelect from '../../components/Select';
import ModalWrapper from '../../components/ModalWrapper';
import DropdownInput from '../../components/DropdownInput';
import Input from '../../components/Input';
import Button from '../../components/Button';

import { useEffect, useMemo, useState, useCallback } from 'react';
import sendRequest from '../../services/service';
import { validateMaxLength } from '../../helpers/mixins';
import { useReportAuth } from '../../hooks/useReportAuth';

interface FilterDrawerProps {
  open: boolean;
  onClose: () => void;
  onFilter: ({ attributeId, value }: FilterQueries) => void;
  appId: string;
}

interface FilterQueries {
  attributeId: string;
  value: string;
}

interface AttributeInterface {
  id: string;
  name: string;
  apiName: string;
  type: 'String' | 'Number' | 'Boolean';
}

export default function FilterDrawer({ open, onClose, onFilter, appId }: FilterDrawerProps) {
  const { data: reportAuth, isLoading: reportAuthLoading } = useReportAuth();
  const [queries, setQueries] = useState<FilterQueries>({
    attributeId: '',
    value: '',
  });

  const [attributes, setAttributes] = useState<AttributeInterface[]>([]);
  const [hasAppliedFilter, setHasAppliedFilter] = useState<boolean>(false);

  const fetchAttributes = useCallback(async () => {
    if (!reportAuth?.accessToken || !appId) return;

    try {
      const res = await sendRequest.get({
        url: 'Attributes/list',
        data: {
          ApplicationId: appId,
        },
        reportAccessToken: reportAuth?.accessToken,
        skipUrlOrganizationId: true,
      });
      const allowedEntityTypes = ['ConsumerSession', 'ConsumerEvent'];
      const excludedKeys = new Set(['ConsumerSession.IntegrationId', 'ConsumerEvent.Id', 'ConsumerEvent.EventId']);
      const allowedTypes = ['String', 'Number', 'Boolean'];
      const allowedApiNames = ['SimpraPropertyName', 'FirstSessionEver', 'SimpraPropertyIdentifier'];

      const parsedAttributes = res
        .filter((attr: any) => allowedEntityTypes.includes(attr.entityType))
        .filter((attr: any) => !excludedKeys.has(attr.key))
        .filter((attr: any) => allowedTypes.includes(attr.type))
        .filter((attr: any) => allowedApiNames.includes(attr.apiName))
        .map((attr: any) => ({
          id: attr.id,
          name: attr.name,
          apiName: attr.apiName,
          type: attr.type,
        }));

      setAttributes(parsedAttributes);
      console.log(parsedAttributes);
    } catch (error) {
      console.error(error);
    }
  }, [reportAuth?.accessToken, appId]);

  useEffect(() => {
    if (open && !reportAuthLoading && reportAuth?.accessToken && appId) {
      fetchAttributes();
    }
  }, [open, reportAuthLoading, reportAuth?.accessToken, appId, fetchAttributes]);

  useEffect(() => {
    if (open && !hasAppliedFilter) {
      setQueries({ attributeId: '', value: '' });
    }
  }, [open, hasAppliedFilter]);

  const selectedAttribute = useMemo(() => {
    return attributes.find((attr) => attr.apiName === queries.attributeId) || null;
  }, [attributes, queries.attributeId]);

  const handleSubmit = async () => {
    onFilter(queries);
    setHasAppliedFilter(true);
    onClose();
  };

  const handleClearFilter = () => {
    setQueries({
      attributeId: '',
      value: '',
    });
    onFilter({ attributeId: '', value: '' });
    setHasAppliedFilter(false);
    onClose();
  };

  return (
    <ModalWrapper
      open={open}
      onClose={onClose}
      title="Filter"
      width={400}
      hideFooter={true}
      loading={false}
    >
      <div className="analytics-filter-form">
        <DropdownInput
          label="Attribute"
          options={attributes.map((attr) => ({
            label: attr.name,
            value: attr.apiName,
          }))}
          value={queries.attributeId || undefined}
          placeholder="Select an attribute to filter"
          onChange={(value) => {
            const newlySelected = attributes.find((a) => a.apiName === value);
            const defaultValue = newlySelected?.type === 'Boolean' ? 'True' : '';
            setQueries({
              value: defaultValue,
              attributeId: value || '',
            });
          }}
          disabled={false}
        />
        {queries.attributeId === '' || selectedAttribute?.type === 'String' || selectedAttribute?.type === 'Number' ? (
          <div className="analytics-filter-input">
            <Input
              value={queries.value}
              onChange={(val) => setQueries({ ...queries, value: val })}
              placeholder="Enter a value"
              error={!validateMaxLength(queries.value, 63, false)}
              errorMessage={queries.value.length > 63 ? 'Value must be 63 characters or less.' : undefined}
              disabled={queries.attributeId === ''}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && queries.attributeId !== '' && queries.value !== '') {
                  handleSubmit();
                }
              }}
            />
          </div>
        ) : (
          <NewSelect
            options={
              selectedAttribute?.type === 'Boolean'
                ? [
                    { label: 'True', value: 'True' },
                    { label: 'False', value: 'False' },
                  ]
                : []
            }
            value={queries.value || null}
            handleChange={(value: string | string[] | null) => setQueries({ ...queries, value: value as string })}
            selectClassName="analytics-filter-select"
            placeholder="Enter a value"
          />
        )}
      </div>
      <div
        className="analytics-filter-bottom"
        style={{ marginTop: '20px', display: 'flex', justifyContent: 'flex-end', gap: '12px' }}
      >
        {hasAppliedFilter && queries.attributeId !== '' && queries.value !== '' && (
          <Button
            theme="danger"
            onClick={handleClearFilter}
          >
            Clear Filter
          </Button>
        )}
        <Button
          theme="secondary"
          onClick={onClose}
        >
          Cancel
        </Button>
        <Button
          theme="primary"
          onClick={handleSubmit}
          disabled={queries.attributeId === '' || queries.value === '' || !validateMaxLength(queries.value, 63, false)}
        >
          Filter
        </Button>
      </div>
    </ModalWrapper>
  );
}
